import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { setupRouteGuards } from './guards'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import KioskLayout from '../layouts/KioskLayout.vue'
import Home from '../pages/Home.vue'
import Modules from '../pages/modules/Modules.vue'
import UserManagement from '../pages/management/UserManagement.vue'
import FactoryDashboard from '../pages/FactoryWeighing/FactoryDashboard.vue'
import FactoryWeigh from '../pages/FactoryWeighing/FactoryWeigh.vue'
import KioskWeigh from '../pages/FactoryWeighing/KioskWeigh.vue'
import FactoryRecords from '../pages/FactoryWeighing/FactoryRecords.vue'

const routes: Array<RouteRecordRaw> = [
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'wim-dashboard' },
    children: [
      {
        path: 'watchdesk',
        name: 'watchdesk',
        redirect: '/watchdesk/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'watchdesk-dashboard',
            component: () => import('../pages/Watchdesk/Dashboard.vue'),
          },
          {
            path: 'reports',
            name: 'watchdesk-reports',
            component: () => import('../pages/Watchdesk/CombinedReports.vue'),
            props: {
              title: 'Watchdesk',
              moduleType: 'watchdesk',
            },
          },
        ],
      },
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'modules',
        path: 'modules',
        component: () => import('../pages/modules/Modules.vue'),
      },
      {
        name: 'module',
        path: 'modules/:id',
        component: () => import('../pages/modules/WimConfiguration.vue'),
        props: true,
      },
      {
        name: 'user-management',
        path: 'management/users',
        component: () => import('../pages/management/UserManagement.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UserManagement.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/system-settings/SystemSettings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FAQ.vue'),
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('../pages/reports/Reports.vue'),
      },
      {
        name: 'audit',
        path: 'audit',
        component: () => import('../pages/audit/AuditLogs.vue'),
      },
      {
        path: 'wim-dashboard',
        name: 'wim-dashboard',
        component: () => import('../pages/WIM/WimDashboard.vue'),
      },
      {
        path: 'wimdata',
        name: 'wimdata',
        component: () => import('../pages/transactions/WimData.vue'),
      },
      {
        path: 'wim-speed',
        name: 'wim-speed',
        component: () => import('../pages/transactions/WimTransactions.vue'),
      },
      {
        path: 'wim-reports',
        name: 'wim-reports',
        component: () => import('../pages/shared/Reports.vue'),
        props: {
          title: 'WIM',
          moduleType: 'wim',
        },
      },
      {
        path: 'wim',
        name: 'wim',
        component: () => import('../pages/transactions/WimTransactions.vue'),
      },
      {
        path: 'national-weighing',
        name: 'national-weighing',
        redirect: '/national-weighing/multideck',
        children: [
          {
            path: 'multideck',
            name: 'multideck',
            component: () => import('../pages/NationalWeighing/Multideck.vue'),
          },
          {
            path: 'lswim',
            name: 'lswim',
            component: () => import('../pages/NationalWeighing/LSWIM.vue'),
          },
          {
            path: 'mobile-weighing',
            name: 'mobile-weighing',
            component: () => import('../pages/NationalWeighing/MobileWeighing.vue'),
          },
          {
            path: 'yard',
            name: 'yard-management',
            component: () => import('../pages/NationalWeighing/YardManagement.vue'),
          },
          {
            path: 'reports',
            name: 'national-weighing-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'National Weighing',
              moduleType: 'national-weighing',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('../pages/Home.vue'),
      },
      {
        path: 'modules',
        name: 'modules',
        component: () => import('../pages/modules/Modules.vue'),
      },
      {
        path: 'management/users',
        name: 'user-management',
        component: () => import('../pages/management/UserManagement.vue'),
      },
      {
        path: 'transactions',
        name: 'transactions',
        component: () => import('../pages/transactions/WimTransactions.vue'),
      },
      {
        path: 'factory',
        name: 'factory-weighing',
        redirect: '/factory/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'factory-dashboard',
            component: () => import('../pages/FactoryWeighing/FactoryDashboard.vue'),
          },
          {
            path: 'weigh',
            name: 'factory-weigh',
            component: () => import('../pages/FactoryWeighing/FactoryWeigh.vue'),
          },
          {
            path: 'mobile-weighing',
            name: 'factory-mobile-weighing',
            component: () => import('../pages/FactoryWeighing/FactoryMobileWeighing.vue'),
          },
          {
            path: 'records',
            name: 'factory-records',
            component: () => import('../pages/FactoryWeighing/FactoryRecords.vue'),
          },
          {
            path: 'kiosk',
            name: 'kiosk-weigh',
            component: () => import('../pages/FactoryWeighing/KioskWeigh.vue'),
          },
          {
            path: 'reports',
            name: 'factory-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Factory Weighing',
              moduleType: 'factory-weighing',
            },
          },
        ],
      },
      {
        path: 'calibration',
        name: 'calibration',
        redirect: '/calibration/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'calibration-dashboard',
            component: () => import('../pages/Calibration/CalibrationDashboard.vue'),
          },
          {
            path: 'records',
            name: 'calibration-records',
            component: () => import('../pages/Calibration/CalibrationRecords.vue'),
          },
        ],
      },
      {
        path: 'vehicle-inspection',
        name: 'vehicle-inspection',
        redirect: '/vehicle-inspection/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'inspection-dashboard',
            component: () => import('../pages/VehicleInspection/Dashboard.vue'),
          },
          {
            path: 'records',
            name: 'inspection-records',
            component: () => import('../pages/VehicleInspection/InspectedRecords.vue'),
          },
          {
            path: 'reports',
            name: 'inspection-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Vehicle Inspection',
              moduleType: 'vehicle-inspection',
            },
          },
        ],
      },
      {
        path: 'parking',
        name: 'parking',
        redirect: '/parking/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'parking-dashboard',
            component: () => import('../pages/Parking/Dashboard.vue'),
          },
          {
            path: 'records',
            name: 'parking-records',
            component: () => import('../pages/Parking/ParkingRecords.vue'),
          },
          {
            path: 'reports',
            name: 'parking-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Parking',
              moduleType: 'parking',
            },
          },
        ],
      },
      {
        path: 'prosecution',
        name: 'prosecution',
        redirect: '/prosecution/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'prosecution-dashboard',
            component: () => import('../pages/Prosecution/Dashboard.vue'),
          },
          {
            path: 'records',
            name: 'prosecution-records',
            component: () => import('../pages/Prosecution/ProsecutionRecords.vue'),
          },
          {
            path: 'reports',
            name: 'prosecution-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Prosecution',
              moduleType: 'prosecution',
            },
          },
          {
            path: 'settings',
            name: 'prosecution-settings',
            component: () => import('../pages/Prosecution/Settings/ProsecutionSettings.vue'),
          },
        ],
      },
      {
        path: 'tolling',
        name: 'tolling',
        redirect: '/tolling/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'tolling-dashboard',
            component: () => import('../pages/Tolling/Dashboard.vue'),
          },
          {
            path: 'records',
            name: 'tolling-records',
            component: () => import('../pages/Tolling/TollingRecords.vue'),
          },
          {
            path: 'rates',
            name: 'tolling-rates',
            component: () => import('../pages/Tolling/TollRates.vue'),
          },
          {
            path: 'booth',
            name: 'tolling-booth',
            component: () => import('../pages/Tolling/TollingBooth.vue'),
          },
          {
            path: 'reports',
            name: 'tolling-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Tolling',
              moduleType: 'tolling',
            },
          },
        ],
      },
      {
        path: 'tags',
        name: 'tags',
        redirect: '/tags/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'tags-dashboard',
            component: () => import('../pages/Tags/Dashboard.vue'),
          },
          {
            path: 'records',
            name: 'tags-records',
            component: () => import('../pages/Tags/TagRecords.vue'),
          },
          {
            path: 'management',
            name: 'tags-management',
            component: () => import('../pages/Tags/TagManagement.vue'),
          },
          {
            path: 'reports',
            name: 'tags-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Tags',
              moduleType: 'tags',
            },
          },
        ],
      },
      {
        path: 'traffic-census',
        name: 'traffic-census',
        redirect: '/traffic-census/dashboard',
        children: [
          {
            path: 'dashboard',
            name: 'census-dashboard',
            component: () => import('../pages/TrafficCensus/Dashboard.vue'),
          },
          {
            path: 'records',
            name: 'census-records',
            component: () => import('../pages/TrafficCensus/CensusRecords.vue'),
          },
          {
            path: 'reports',
            name: 'census-reports',
            component: () => import('../pages/shared/Reports.vue'),
            props: {
              title: 'Traffic Census',
              moduleType: 'traffic-census',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Setup route guards
setupRouteGuards(router)

export default router
